<template>
  <main>
    <div class="header__wrapper">
      <!-- Back button - Title - Last time saved -->
      <header class="header__details">
        <a-button type="link" class="back-button" @click="handleBack">
          <ArrowLeftOutlined />
          Volver
        </a-button>

        <div class="title">
          <h2 class="name">{{ formDetail?.name || "Cargando..." }}</h2>
        </div>

        <div v-if="formDetail" class="save-time">
          Guardado por ultima vez:
          {{ dateTimeToSlashFormat(formDetail.updated_at, true) || "..." }}
        </div>
      </header>
    </div>

    <!-- Tabs - Status switch -->
    <a-spin :spinning="loadingForm">
      <a-tabs v-if="formDetail" v-model:activeKey="currentSection" centered destroyInactiveTabPane>
        <template #rightExtra>
          <div class="extra-action">
            <a-tooltip
              :title="
                isPreviewEnable
                  ? null
                  : 'Recuerda guardar tus cambios para habilitar la vista previa.'
              "
              arrowPointAtCenter
              placement="topRight"
            >
              <a-button @click="handlePreview" :disabled="!isPreviewEnable">
                Vista previa
                <EyeOutlined />
              </a-button>
            </a-tooltip>
          </div>
        </template>

        <a-tab-pane :key="SectionMenus.Build" tab="Construir formulario">
          <FormEditorSection
            @update="fetchDetail"
            @toggle:preview="(enable) => handleTogglePreview(enable)"
          />
        </a-tab-pane>

        <a-tab-pane :key="SectionMenus.Conditions" tab="Condiciones lógicas">
          <div class="container-tab">
            <FormConditionsSection @update="fetchDetail" />
          </div>
        </a-tab-pane>

        <a-tab-pane :key="SectionMenus.Edit" tab="Datos generales">
          <div class="container-tab">
            <h3>Datos generales</h3>
            <FormDetailsSection @update="fetchDetail" />
          </div>
        </a-tab-pane>
      </a-tabs>
    </a-spin>

    <PreviewModal v-if="formDetail" v-model:visible="previewVisible" :form="formDetail" />
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, provide } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ArrowLeftOutlined, EyeOutlined } from "@ant-design/icons-vue";

import { Form } from "@/@types/entities/forms";
import { formService } from "@/app/entities/services";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";

import FormEditorSection from "@/app/entities/components/sections/forms/FormEditorSection.vue";
import FormDetailsSection from "@/app/entities/components/sections/forms/FormDetailsSection.vue";
import FormConditionsSection from "@/app/entities/components/sections/forms/FormConditionsSection.vue";
import PreviewModal from "@/app/entities/components/modals/forms/PreviewModal.vue";

enum SectionMenus {
  Build = "build",
  Edit = "edit",
  Conditions = "conditions",
}

const route = useRoute();
const router = useRouter();

const currentSection = ref<SectionMenus>(SectionMenus.Build);
const isPreviewEnable = ref(true);

const formDetail = ref<Form | undefined>(undefined);
const loadingForm = ref(false);
provide("formDetail", formDetail);
provide("loadingForm", loadingForm);

const fetchDetail = async () => {
  loadingForm.value = true;

  formDetail.value = await formService.getDetails(Number(route.params.id));

  loadingForm.value = false;
};

const handleTogglePreview = (enable: boolean) => {
  isPreviewEnable.value = enable;
};

onMounted(async () => {
  await fetchDetail();
});

const handleBack = () => {
  router.push("/operations/forms");
};

// Preview
const previewVisible = ref(false);

const handlePreview = () => {
  previewVisible.value = true;
};
</script>

<style scoped lang="sass">
h3
  font-size: 24px
  font-weight: 600
  color: $gray-8
  margin: 0

main
  background-color: $geekblue-1
  height: 100%

.header
  &__wrapper
    width: 100%
    padding: 6px 12px 0
    background-color: $white

  &__details
    display: flex
    justify-content: center
    align-items: center
    position: relative
    width: 100%

    .back-button
      padding: 0
      position: absolute
      left: 0

    .title
      display: flex
      align-items: center
      gap: 8px

      .name
        font-size: 16px
        font-weight: 600
        color: $gray-9
        margin: 0

    .save-time
      font-style: italic
      font-weight: 300
      font-size: 12px
      position: absolute
      right: 0

.extra-action
  display: flex

:deep(.ant-tabs-nav)
  background-color: $white
  // margin-bottom: 12px !important
  margin-bottom: 0 !important

:deep(.ant-tabs)
  height: 100%

:deep(.ant-tabs-content)
  height: 100%

:deep(.ant-tabs-extra-content)
  position: absolute
  right: 12px

:deep(.ant-tabs-tabpane)
  height: 100%

// :deep(.ant-tabs-tabpane:not(:first-child))
//   background-color: $white
.ant-spin-nested-loading
  height: calc(100% - 30px)

:deep(.ant-spin-container)
  height: 100%

.container-tab
  margin: 1rem auto 0
  max-width: 50%
  height: calc(100% - 1rem)

footer
  margin: 0 auto
</style>
